import * as React from 'react';
import styled from 'styled-components';

import { loadAppData, fitPVDiagram, setPlanTier, setSample, setEntity, getTaskAttributeCount, setAiBillingAccess } from '@process-advisor/process-miner/processMiner/processMiner';

import { Visualizer } from '@process-advisor/process-miner/components/visualizer';
import { Sidebar } from '@process-advisor/process-miner/navigation/sidebar/sidebar';
import { SlidingPanel } from '@process-advisor/process-miner/components/slidingPanel/slidingPanel';
import { TopBar } from '@process-advisor/process-miner/navigation/topBar/topBar';
import { ColorPicker } from '@process-advisor/process-miner/components/colorPicker/colorPicker';
import { Spinner } from '@process-advisor/process-miner/components/spinner/spinner';
import { ContextMenu } from '@process-advisor/process-miner/components/contextMenu/contextMenu';
import { StepCostModal } from '@process-advisor/process-miner/modals/cost/stepCostModal';
import { StepMetricsModal } from '@process-advisor/process-miner/modals/metrics/stepMetricsModal';

import { ActivityStepAnalysisModal } from '@process-advisor/process-miner/modals/activityStepsAnalysis';
import { ProcessAnalyticsModal } from '@process-advisor/process-miner/modals/history/processAnalytics';
import { HistogramModal } from '@process-advisor/process-miner/modals/histogram/histogram';
import { CommentsModal } from '@process-advisor/process-miner/modals/comments/commentsModal';
import { AnalyticModal } from '@process-advisor/process-miner/modals/analytic/analyticModal';
import { TaskDetailModal } from '@process-advisor/process-miner/modals/detail/taskDetail/taskDetailModal';
import { EventDetailModal } from '@process-advisor/process-miner/modals/detail/eventDetail/eventDetailModal';
import { ViewWorkflowModal } from '@process-advisor/process-miner/modals/workflow/viewWorkflow';
import { VariantLifecycleModal } from '@process-advisor/process-miner/modals/lifecycle/variantLifecycleModal';
import { TaskLifecycleModal } from '@process-advisor/process-miner/modals/lifecycle/taskLifecycleModal';
import { LifecycleModalSingle } from '@process-advisor/process-miner/time/lifecycle/lifecycleModalSingle';
import { DiscoveryRuleModal } from '@process-advisor/process-miner/modals/discoveryRules/discoveryRuleModal';
import { FindingModal } from '@process-advisor/process-miner/modals/findings/findingModal';
import { SaveModal } from '@process-advisor/process-miner/modals/save/saveModal';
import { DownloadDiscoveryRule } from '@process-advisor/process-miner/discoveryRules/downloadDiscoveryRule';
import { DownloadFinding } from '@process-advisor/process-miner/findings/downloadFinding';

import { ImportCSVStepper } from '@process-advisor/process-miner/eventLog/importCSVStepper';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { Event } from '@process-advisor/process-miner/api/events';
import { ActionButtons } from '@process-advisor/process-miner/navigation/actions/actionButtonsNav'
import AppNav from '@process-advisor/process-miner/navigation/AppNav';
import { ProgressModal } from '@process-advisor/process-miner/modals/progress/progress';
import { NoEvents } from '@process-advisor/process-miner/components/noEvents';
import { setAiBillingAccessContext } from '@process-advisor/process-miner/consultant/aiBillingAccess';

const noopModule = {
  invoke: () => Promise.resolve({ active: true, type: 'COMMERCIAL', items: [], result: [], customer_id: '' }),
  requestJira: () => Promise.resolve({
    json: () => Promise.resolve({ groups: { items: [] }, accountId: '', values: [], siteUrl: '' })
  }),
  view: { getContext: () => Promise.resolve({ siteUrl: '', localId: '' }) },
  router: { navigate: () => Promise.resolve() },
};
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) {
    try {
      const mod = await import('@forge/bridge');
      forgeModule = (typeof mod.invoke === 'function') ? mod : noopModule;
    } catch(e) {
      forgeModule = noopModule;
    }
  }
  return forgeModule;
};

const Container = styled.div<{ environment: string | undefined}>`
    width: 100%;
    display: flex;
    flex-direction: column;
    font-family: system-ui;
    border: ${props => props.environment === 'Jira' ? '2px solid darkgrey': ''}
    height: ${props => (props.environment === 'Jira' || props.environment === 'WEB') ? '100%' : '100vh'}
`;

const Wrapper1 = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 62px 1fr;
`;

const LeftWrapper = styled.div<{ color: string }>`
    grid-column: 1 ;
    grid-row: 1 / 1;
    background-color: ${({ color }) => color};
`;

const RightWrapper1 = styled.div`
    grid-column: 2;
    grid-row: 1 / 1;
    align-items: center;
`;

const ContainerEL = styled.div`
    align-items: center;
    justify-content: center;
    margin-left: 62px;
`;


type MetricusContainerProps = {
    app?: string;
    eventLogID?: string;
    modelID?: string;
    planTier?: string;
    sample?: string;
    activeComponent?: (value: string) => void;
    model?: any
    title?: any
    license?: any
    licenseType?: any
    contextPath?: any
    refreshData?: boolean
    appTitle?: string
    AppVersion?: string
    customerID?: string
    closePM?: any
    stagilFieldsDC?: any
    visualizerMode?: any
    customer?: any,
    fields?: any,
    AI?: any,
    processLabel?: string,
    entityLabel?: string
    forgeContext?: any
    licenseInfo?: any
    appEdition?: string
    editionAccess?: any
    requestedMode?: string
    hidePrimaryHeader?: boolean
};


const isTruthyFlag = (value: any) => (
    value === true
    || value === 1
    || value === '1'
    || String(value ?? '').trim().toLowerCase() === 'true'
);

const snDisplayValue = (value: any) => (
    value && typeof value === 'object'
        ? (value.value ?? value.display_value ?? '')
        : value
);


const ASSESSMENT_CREDIT_VALID_DAYS = 60;
const ASSESSMENT_CREDIT_VALID_MS = ASSESSMENT_CREDIT_VALID_DAYS * 24 * 60 * 60 * 1000;

const parseAssessmentCreditDate = (value: any): Date | null => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(String(value));
    return Number.isFinite(date.getTime()) ? date : null;
};

const getAssessmentCreditAuthorizedAt = (source: any = {}) => (
    source?.authorizedAt
    || source?.ai_credit_authorized_at
    || source?.assessmentCreditAuthorizedAt
    || source?.createdAt
    || source?.authorization?.createdAt
    || source?.authorization?.authorizedAt
    || source?.authorization?.marker?.createdAt
    || source?.marker?.createdAt
    || source?.assessmentCreditMarker?.createdAt
    || null
);

const getAssessmentCreditExpiresAt = (source: any = {}): Date | null => {
    const explicit = parseAssessmentCreditDate(source?.expiresAt || source?.validUntil || source?.assessmentCreditExpiresAt || source?.authorization?.expiresAt || source?.authorization?.marker?.expiresAt || source?.marker?.expiresAt || source?.assessmentCreditMarker?.expiresAt);
    if (explicit) return explicit;
    const authorizedAt = parseAssessmentCreditDate(getAssessmentCreditAuthorizedAt(source));
    return authorizedAt ? new Date(authorizedAt.getTime() + ASSESSMENT_CREDIT_VALID_MS) : null;
};

const isAssessmentCreditAuthorizationExpired = (source: any = {}) => {
    const expiresAt = getAssessmentCreditExpiresAt(source);
    return expiresAt ? Date.now() > expiresAt.getTime() : false;
};

const isSampleFlag = (sampleValue: any, categoryValue?: any) => {
    const sample = snDisplayValue(sampleValue);
    const category = snDisplayValue(categoryValue);
    return sample === true
        || sample === 1
        || sample === '1'
        || String(sample ?? '').trim().toUpperCase() === 'Y'
        || String(sample ?? '').trim().toLowerCase() === 'true'
        || String(category ?? '').trim().toLowerCase() === 'sample';
};

const applySampleAccess = (access: any = {}, model: any = {}) => ({
    ...(access || {}),
    sample: snDisplayValue(model?.sample) || 'Y',
    sampleDataset: true,
    isSample: true,
    requiresAssessmentCredits: false,
    isUsageLimited: false,
    isLimitedAccess: false,
    enforceWorkItemLimit: false,
    workItemLimit: null,
    taskLimit: null,
    showPlanBadge: false,
    showLimitLine: false,
});

const isCreditUnlocked = (access: any = {}) => (
    (isTruthyFlag(access?.assessmentCreditAuthorized)
    || isTruthyFlag(access?.hasAssessmentCreditAuthorization)
    || isTruthyFlag(access?.datasetAiUnlocked)
    || isTruthyFlag(access?.aiCreditAuthorized))
    && !isAssessmentCreditAuthorizationExpired(access)
);

const isModelCreditUnlocked = (model: any = {}) => (
    (isTruthyFlag(model?.ai_credit_authorized)
    || isTruthyFlag(model?.aiCreditAuthorized)
    || isTruthyFlag(model?.assessmentCreditAuthorized))
    && !isAssessmentCreditAuthorizationExpired(model)
);

const buildAssessmentCreditDetailFromModel = (model: any = {}, modelID?: string, title?: any) => ({
    modelId: String(modelID || model?.id || ''),
    processModelId: String(modelID || model?.id || ''),
    datasetId: String(modelID || model?.id || ''),
    modelTitle: title || model?.name || '',
    requiredCredits: Number(model?.ai_credit_required_count || model?.ai_credit_authorized_count || 1) || 1,
    authorization: {
        authorized: true,
        authorizationId: model?.ai_credit_authorization_id || null,
        authorizedCreditCount: Number(model?.ai_credit_authorized_count || model?.ai_credit_required_count || 1) || 1,
        requiredCredits: Number(model?.ai_credit_required_count || model?.ai_credit_authorized_count || 1) || 1,
    },
    createdAt: model?.ai_credit_authorized_at || null,
    authorizedAt: model?.ai_credit_authorized_at || null,
    expiresAt: getAssessmentCreditExpiresAt(model)?.toISOString?.() || null,
});

const applyAssessmentCreditUnlockToAccess = (access: any = {}, detail: any = {}) => ({
    ...(access || {}),
    assessmentCreditAuthorized: true,
    hasAssessmentCreditAuthorization: true,
    datasetAiUnlocked: true,
    aiCreditAuthorized: true,
    assessmentCreditMarker: detail?.authorization?.marker || detail?.authorization || detail || null,
    assessmentCreditAuthorizedAt: getAssessmentCreditAuthorizedAt(detail?.authorization?.marker || detail?.authorization || detail) || null,
    assessmentCreditExpiresAt: getAssessmentCreditExpiresAt(detail?.authorization?.marker || detail?.authorization || detail)?.toISOString?.() || null,
    isUsageLimited: false,
    isLimitedAccess: false,
    enforceWorkItemLimit: false,
    workItemLimit: null,
    taskLimit: null,
    showPlanBadge: false,
    showLimitLine: false,
});

const deriveEditionAccessFromModel = (access: any = {}, model: any = {}, modelID?: string, title?: any) => {
    if (isSampleFlag((access as any)?.sample ?? model?.sample, (access as any)?.category ?? model?.category)) {
        return applySampleAccess(access, { ...(model || {}), sample: (access as any)?.sample ?? model?.sample });
    }
    if (isCreditUnlocked(access)) {
        return applyAssessmentCreditUnlockToAccess(access, access?.assessmentCreditMarker || {});
    }
    if (isModelCreditUnlocked(model)) {
        return applyAssessmentCreditUnlockToAccess(access, buildAssessmentCreditDetailFromModel(model, modelID, title));
    }
    return access || {};
};

export const Metricus: React.FC<MetricusContainerProps> = ({ app, eventLogID, modelID, planTier, sample, activeComponent , model, title, license, licenseType, contextPath, refreshData, appTitle, AppVersion, customerID, closePM, stagilFieldsDC,visualizerMode,customer,fields,AI, processLabel='Process', entityLabel='Work Item', forgeContext, licenseInfo, appEdition='standard', editionAccess, requestedMode, hidePrimaryHeader=false}) => {    
    const initialEditionAccess = deriveEditionAccessFromModel(editionAccess || {}, model || {}, modelID, title);
    const [isDataLoaded, setIsDataLoaded] = React.useState<boolean>(false);
    const [loadError, setLoadError] = React.useState<string>('');
    const [loadEventLog, setLoadEventLog] = React.useState<boolean>(true);
    const [runtimeEditionAccess, setRuntimeEditionAccess] = React.useState<any>(initialEditionAccess);
    const runtimeEditionAccessRef = React.useRef<any>(initialEditionAccess);

    React.useEffect(() => {
        runtimeEditionAccessRef.current = runtimeEditionAccess || editionAccess || {};
    }, [runtimeEditionAccess, editionAccess]);

    React.useEffect(() => {
        const nextAccess = deriveEditionAccessFromModel(editionAccess || {}, model || {}, modelID, title);
        runtimeEditionAccessRef.current = nextAccess;
        setRuntimeEditionAccess(nextAccess);
    }, [editionAccess, modelID, title, (model as any)?.ai_credit_authorized, (model as any)?.ai_credit_authorization_id]);

    React.useEffect(() => {
        setEntity({ entity: entityLabel, initial: false });
    }, [entityLabel]);

    const [ActionBarBackground, setActionBarBackground] = React.useState<string>('');
    const [statusChanged, setStatusChanged] = React.useState<boolean>(false);
    const [events, setEvents] = React.useState<number>(1);
    const [mode, setMode] = React.useState<string>('');
    const [modelStats, setModelStats] = React.useState([]);
    const [maxWidth, setMaxWidth] = React.useState<string>('false');
    const [modelTitle, setModelTitle] = React.useState<string>('');
    const [width, setWidth] = React.useState<number>(50);
    const [snapshotCount, setSnapshotCount] = React.useState<number>(0);
    const [snapshotPanelOpen, setSnapshotPanelOpen] = React.useState<boolean>(false);
    const noEventsNotifiedRef = React.useRef<boolean>(false);

    const onDragEnd = ([, secondWidth]) => {
        apiProvider.eventEmitter.emit(Event.resizeSidebar, secondWidth);
        if (apiProvider.getActiveAction() === 'process_miner') fitPVDiagram();
    };
    setPlanTier(planTier);
    setSample(sample);

    const sleep = ms => new Promise(r => setTimeout(r, ms));
    
    const help = async () => {
        if (AppVersion === 'FORGE') {
            const { router } = await loadForgeModule();
            router.open('https://metricus.atlassian.net/wiki/spaces/POJ');
        } else {
            window.open('https://metricus.atlassian.net/wiki/spaces/POJ', '_blank');
        }
    };

    const loadData = async (overrideAccess?: any, overrideRefreshData?: boolean) => {
        setIsDataLoaded(false);
        setLoadError('');
        try {
            const sampleFlag = snDisplayValue(sample ?? (model as any)?.sample ?? 'N');
            const sampleDataset = isSampleFlag(sampleFlag, (model as any)?.category);
            const baseAccess = deriveEditionAccessFromModel(overrideAccess || runtimeEditionAccessRef.current || editionAccess || {}, model || {}, modelID, title);
            const effectiveEditionAccess = {
                ...(baseAccess || {}),
                sample: sampleFlag,
                sampleDataset,
            };
            runtimeEditionAccessRef.current = effectiveEditionAccess;
            setRuntimeEditionAccess(effectiveEditionAccess);
            const effectiveRefreshData = typeof overrideRefreshData === 'boolean' ? overrideRefreshData : refreshData;
            let tempStats = await loadAppData(AppVersion,modelID,model,title,contextPath,effectiveRefreshData,license, customerID,app,appTitle,stagilFieldsDC,fields, effectiveEditionAccess);
            setModelStats(tempStats);

            const stats = tempStats?.[0] || {};
            const billingRecordCount = Number(
                (model as any)?.total_issues
                ?? (model as any)?.total_tasks
                ?? (model as any)?.work_items
                ?? (model as any)?.workItems
                ?? (model as any)?.filtered
                ?? stats.total_tasks
                ?? stats.tasks_total
                ?? stats.tasks
                ?? 0
            ) || 0;
            const billingSample = snDisplayValue(stats.sample ?? sample ?? (model as any)?.sample ?? 'N');
            const billingSampleDataset = isSampleFlag(billingSample, (model as any)?.category);
            const aiBillingContext = {
                AppVersion,
                licenseInfo,
                planAccess: effectiveEditionAccess,
                customerID,
                modelId: modelID,
                modelTitle: title,
                recordCount: billingRecordCount,
                sample: billingSample,
                sampleDataset: billingSampleDataset,
            };
            setAiBillingAccess(aiBillingContext);
            setAiBillingAccessContext(aiBillingContext);

            console.log('[Metricus] customer props', { customer, customerID });
            console.log('[Metricus] AI billing context', aiBillingContext);

            setEvents(tempStats[0].events);
            setStatusChanged(false);
        } catch (error) {
            // pako errors use error.msg; standard errors use error.message
            const errMsg = (error as any)?.message || (error as any)?.msg || String(error);
            console.error('loadData failed:', errMsg, error);
            setLoadError(errMsg);
            setStatusChanged(false);
            setEvents(1);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('process-advisor-snow-processing-error', {
                    detail: { reason: 'load-failed', type: 'load-failed', message: errMsg, modelID, title },
                }));
            }
            return;
        } finally {
            setIsDataLoaded(true);
        }
    };


    React.useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onAssessmentCreditAuthorized = (event: any) => {
            const detail = event?.detail || {};
            const eventModelId = String(detail.modelId || detail.processModelId || detail.datasetId || '');
            if (eventModelId && String(modelID || '') && eventModelId !== String(modelID || '')) return;
            const unlockedAccess = applyAssessmentCreditUnlockToAccess(runtimeEditionAccessRef.current || editionAccess || {}, detail);
            runtimeEditionAccessRef.current = unlockedAccess;
            setRuntimeEditionAccess(unlockedAccess);
            if (!detail.initialCheck) {
                // Assessment Credits remove the 100-work-item cap, but the saved event log may
                // already be the capped 100-item copy. Force a fresh Jira refresh immediately
                // so the full unlocked dataset is rebuilt and saved.
                loadData(unlockedAccess, true);
            }
        };
        window.addEventListener('metricus-assessment-credit-authorized', onAssessmentCreditAuthorized as EventListener);
        return () => window.removeEventListener('metricus-assessment-credit-authorized', onAssessmentCreditAuthorized as EventListener);
    }, [modelID, editionAccess]);

    React.useEffect(() => {

        setActionBarBackground('#F5F5F5');
        loadData(deriveEditionAccessFromModel(runtimeEditionAccessRef.current || editionAccess || {}, model || {}, modelID, title));
        setModelTitle(title)
    }, []);

    React.useEffect(() => {
        const nextAccess = deriveEditionAccessFromModel(runtimeEditionAccessRef.current || editionAccess || {}, model || {}, modelID, title);
        runtimeEditionAccessRef.current = nextAccess;
        setRuntimeEditionAccess(nextAccess);
        // Do not reload here. Initial load already receives the selected dataset's unlock state.
        // Reloading after isDataLoaded caused authorised models to be processed twice.
    }, [modelID, title, (model as any)?.ai_credit_authorized, (model as any)?.ai_credit_authorization_id, (model as any)?.ai_credit_authorized_count]);


    React.useEffect(() => {
        if (mode === 'Event Log Loaded') setLoadEventLog(true)
        visualizerMode(mode)
    }, [mode]);

    React.useEffect(() => {
        const listener = () => {
            setActionBarBackground('#F5F5F5');
        };
        listener();

        apiProvider.on(Event.changeVisualizerBackgroundColor, listener);

        return () => {
            apiProvider.off(Event.changeVisualizerBackgroundColor, listener);
        };
    }, []);

    React.useEffect(() => {
        const listener = () => {
            setLoadEventLog(true);
        };
        apiProvider.on(Event.displayVisualizer, listener);

        return () => {
            apiProvider.off(Event.displayVisualizer, listener);
        };
    }, []);

    React.useEffect(() => {
        const listener = () => {
            setMaxWidth('true')
        };
        apiProvider.on(Event.setMaxWidth, listener);

        return () => {
            apiProvider.off(Event.setMaxWidth, listener);
        };
    }, []);

    React.useEffect(() => {
        const listener = () => {
            setMaxWidth('false')
        };
        apiProvider.on(Event.setVariableWidth, listener);

        return () => {
            apiProvider.off(Event.setVariableWidth, listener);
        };
    }, []);

    React.useEffect(() => {
        setMode('home')
    }, [app]);

    React.useEffect(() => {
        const listener = () => {
            closePM(true)
        };
        apiProvider.on(Event.closePM, listener)

        return () => {
            apiProvider.off(Event.closePM, listener);
        };
    }, []);


    React.useEffect(() => {
        const isSnow = String(AppVersion || '').toUpperCase() === 'SNOW';
        if (!isSnow || !isDataLoaded || events !== 0) {
            if (events !== 0) noEventsNotifiedRef.current = false;
            return;
        }
        if (noEventsNotifiedRef.current) return;
        noEventsNotifiedRef.current = true;

        const detail = {
            reason: 'no-events',
            type: 'no-events',
            message: `No events were found for the activity type saved against this ${processLabel} Model.`,
            details: { modelID, title },
        };

        if (typeof closePM === 'function') {
            closePM(detail);
        } else if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('process-advisor-snow-processing-error', { detail }));
        }
    }, [AppVersion, isDataLoaded, events, closePM, modelID, title, processLabel]);

    if (loadError) {
        return (
            <div style={{ minHeight: '100vh', background: '#F7F8FA', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ width: 'min(720px, 100%)', background: '#FFFFFF', border: '1px solid #DFE1E6', borderRadius: 10, boxShadow: '0 6px 20px rgba(9,30,66,0.12)', padding: 24 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#DE350B', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>Process Advisor</div>
                    <h2 style={{ margin: '0 0 8px', color: '#172B4D', fontSize: 22 }}>Model processing failed</h2>
                    <p style={{ margin: '0 0 14px', color: '#42526E', lineHeight: 1.5 }}>
                        Process Advisor could not finish refreshing this {processLabel.toLowerCase()} model. The processing spinner has been closed so you can retry or return to the model list.
                    </p>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#FFF4F2', color: '#AE2E24', border: '1px solid #FFD5D2', borderRadius: 8, padding: 12, fontSize: 12, lineHeight: 1.45, maxHeight: 180, overflow: 'auto' }}>{loadError}</pre>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                        <button type="button" onClick={() => typeof closePM === 'function' && closePM(true)} style={{ border: '1px solid #DFE1E6', background: '#FFFFFF', color: '#172B4D', borderRadius: 6, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}>Back to Models</button>
                        <button type="button" onClick={() => loadData(deriveEditionAccessFromModel(runtimeEditionAccessRef.current || editionAccess || {}, model || {}, modelID, title), true)} style={{ border: '1px solid #0C66E4', background: '#0C66E4', color: '#FFFFFF', borderRadius: 6, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}>Retry refresh</button>
                    </div>
                </div>
            </div>
        );
    }

    if (events === 0) {
        if (String(AppVersion || '').toUpperCase() === 'SNOW') return null;
        return <NoEvents processLabel={processLabel}/>
    }

    if (!isDataLoaded || statusChanged) {
        return <Spinner/>
    }
    return (
        <Container environment={app}>


            {!loadEventLog ? 
                <ContainerEL>
                    <ImportCSVStepper app={app} mode={setMode} customerID={customerID} AppVersion={AppVersion} processLabel={processLabel}/>
                </ContainerEL>
            : 
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: app === 'Jira' ? '100%' : '100vh' }}>
                    <AppNav
                        currentMode={mode}
                        customer={customer}
                        onModeChange={setMode}
                        onSave={async () => { await saveModel(AppVersion); }}
                        onExit={() => closePM(true)}
                        onHelp={help}
                        appTitle={appTitle}
                        modelTitle={modelTitle}
                        hasTaskAttributes={getTaskAttributeCount() > 0}
                        AppVersion={AppVersion}
                        appEdition={appEdition}
                        editionAccess={runtimeEditionAccess}
                        licenseInfo={licenseInfo}
                        snapshotCount={snapshotCount}
                        onSnapshotsClick={() => setSnapshotPanelOpen(true)}
                        hidePrimaryHeader={hidePrimaryHeader}
                        externalModeRequest={requestedMode}
                    />
                    <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
                        {mode === 'process_miner' ? (
                            <SlidingPanel onDragEnd={onDragEnd} max={maxWidth} width={width} mode={mode} >
                                <Visualizer visible={true} model={model} app={app} mode={mode} customer_id={customerID} customer={customer} loadEventLog={loadEventLog} AppVersion={AppVersion} contextPath={contextPath} AI={AI} license={license} licenseType={licenseType} processLabel={processLabel} entityLabel={entityLabel} forgeContext={forgeContext} licenseInfo={licenseInfo} appEdition={appEdition} editionAccess={runtimeEditionAccess} onModeChange={setMode} appTitle={appTitle} snapshotCount={snapshotCount} setSnapshotCount={setSnapshotCount} snapshotPanelOpen={snapshotPanelOpen} setSnapshotPanelOpen={setSnapshotPanelOpen}/>
                                <Sidebar app={app} modelID={modelID} mode={mode} processLabel={processLabel}  />
                            </SlidingPanel>
                        ) : (
                            <SlidingPanel onDragEnd={onDragEnd} max={maxWidth} width={width} mode={mode} >
                                <Visualizer visible={true} model={model} app={app} mode={mode} customer_id={customerID} customer={customer} loadEventLog={loadEventLog} AppVersion={AppVersion} contextPath={contextPath} AI={AI} license={license} licenseType={licenseType} processLabel={processLabel} entityLabel={entityLabel} forgeContext={forgeContext} licenseInfo={licenseInfo} appEdition={appEdition} editionAccess={runtimeEditionAccess} onModeChange={setMode} appTitle={appTitle} snapshotCount={snapshotCount} setSnapshotCount={setSnapshotCount} snapshotPanelOpen={snapshotPanelOpen} setSnapshotPanelOpen={setSnapshotPanelOpen}/>
                            </SlidingPanel>
                        )}
                    </div>
                </div>
            }

            <ColorPicker />
            <ContextMenu />
            <StepCostModal />
            <StepMetricsModal/>
            <CommentsModal />
            <AnalyticModal />
            <ActivityStepAnalysisModal />
            <ProcessAnalyticsModal />
            <HistogramModal />
            <DiscoveryRuleModal/>
            <FindingModal/>
            <TaskDetailModal/>
            <EventDetailModal/>
            <VariantLifecycleModal/>
            <TaskLifecycleModal/>
            <LifecycleModalSingle/>
            <ProgressModal/>
            <SaveModal AppVersion={AppVersion}/>    
            <ViewWorkflowModal/>	
            <div
                aria-hidden="true"
                style={{
                    position: 'fixed',
                    left: '-12000px',
                    top: 0,
                    width: '640px',
                    pointerEvents: 'none',
                    zIndex: -1,
                }}
            >
                <DownloadDiscoveryRule/>
                <DownloadFinding/>
            </div>
        </Container>
    );
};
